import React, { useEffect, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { Form, Row, Col, Card, Button, Spinner, Alert } from 'react-bootstrap';
import PageHeader from '../components/PageHeader';
import { adminApi, extractError } from '../api/client';
import { useToast } from '../contexts/ToastContext';
import { useCounties } from '../hooks/useLocations';
import { SOURCE_TYPES, PARSING_METHODS, DS_STATUSES, dsStatusMeta } from '../lib/dataSourceMeta';
import { fmtDate } from '../lib/lookups';

const EMPTY = {
  name: '',
  source_type: '',
  source_url: '',
  parsing_method: 'manual',
  import_frequency: '',
  field_mapping_notes: '',
  import_rules: '',
  county_id: '',
  status: 'active',
  cadence_enabled: false,
  kind: '',
  // Phase B — adapter framework
  adapter_name: '',
  config_json: '',
  auto_apply: false,
};

export default function DataSourceFormPage({ mode }) {
  const { stateId, sourceId } = useParams();
  const navigate = useNavigate();
  const toast = useToast();

  const [state, setState] = useState(null);
  const [data, setData] = useState(EMPTY);
  const [original, setOriginal] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [errors, setErrors] = useState({});
  const [actioning, setActioning] = useState(false);
  const [runningAdapter, setRunningAdapter] = useState(false);
  const [recentJobs, setRecentJobs] = useState([]);
  const [adapters, setAdapters] = useState([]);

  const { counties } = useCounties(stateId);

  async function loadRecentJobs() {
    if (mode !== 'edit') return;
    try {
      const res = await adminApi.get(`/admin/states/${stateId}/import-jobs`, {
        params: { data_source_id: sourceId, page: 1, page_size: 5 },
      });
      setRecentJobs(res.data.items || []);
    } catch (_e) {
      // non-critical
    }
  }

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const promises = [adminApi.get(`/admin/states/${stateId}`)];
        if (mode === 'edit') promises.push(adminApi.get(`/admin/states/${stateId}/data-sources/${sourceId}`));
        const res = await Promise.all(promises);
        if (!active) return;
        setState(res[0].data);
        if (mode === 'edit') {
          const d = res[1].data;
          setData({
            ...EMPTY, ...d,
            county_id: d.county_id || '',
            config_json: d.config ? JSON.stringify(d.config, null, 2) : '',
          });
          setOriginal(d);
          loadRecentJobs();
        }
      } catch (e) {
        if (active) setError(extractError(e));
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [stateId, sourceId, mode]);

  // Load the registry of built-in adapters (Phase B) once.
  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const res = await adminApi.get('/admin/adapters');
        if (active) setAdapters(res.data.items || []);
      } catch (_e) {
        // Non-fatal — admin can still type the adapter name manually.
      }
    })();
    return () => { active = false; };
  }, []);

  async function runAdapter() {
    if (!data.adapter_name || mode !== 'edit') return;
    // Validate JSON config before firing.
    if (data.config_json && data.config_json.trim()) {
      try { JSON.parse(data.config_json); }
      catch (_e) { toast.error('Adapter config must be valid JSON — save first.'); return; }
    }
    setRunningAdapter(true);
    try {
      const res = await adminApi.post(
        `/admin/adapters/${data.adapter_name}/run`,
        null,
        { params: { data_source_id: sourceId } },
      );
      const r = res.data || {};
      if (r.ok) {
        toast.success(
          `Adapter ran — fetched ${r.fetched ?? 0}, staged ${r.staged ?? 0}` +
          (r.failed ? `, failed ${r.failed}` : '') +
          (data.auto_apply ? ' (auto-applied)' : ' (review in Staging Inbox)'),
        );
      } else {
        toast.error(`Adapter run failed: ${r.error || 'unknown error'}`);
      }
      // Refresh the source so last_run_status updates.
      const fresh = await adminApi.get(`/admin/states/${stateId}/data-sources/${sourceId}`);
      setData({
        ...EMPTY, ...fresh.data,
        county_id: fresh.data.county_id || '',
        config_json: fresh.data.config ? JSON.stringify(fresh.data.config, null, 2) : '',
      });
      setOriginal(fresh.data);
    } catch (e) {
      toast.error(extractError(e));
    } finally {
      setRunningAdapter(false);
    }
  }

  function update(field, value) { setData((d) => ({ ...d, [field]: value })); }

  function validate() {
    const errs = {};
    if (!data.name?.trim()) errs.name = 'Required';
    if (!data.source_type) errs.source_type = 'Required';
    if (!data.source_url?.trim()) errs.source_url = 'Required';
    if (data.source_url && !/^https?:\/\//i.test(data.source_url)) errs.source_url = 'Must start with http(s)://';
    setErrors(errs);
    return Object.keys(errs).length === 0;
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');
    if (!validate()) {
      setError('Please correct the highlighted fields.');
      return;
    }
    setSaving(true);
    try {
      // Send empty string for an unset county; the backend treats both null
      // and "" as "clear the county scope" (state-level).
      const payload = { ...data, county_id: data.county_id || '' };
      // The backend models `kind` and `adapter_name` as Literal/optional —
      // an empty string is NOT a valid Literal value. Coerce blanks to null
      // so Pydantic accepts them as "unset".
      if (!payload.kind) payload.kind = null;
      if (!payload.adapter_name) payload.adapter_name = null;
      // Parse config_json → config dict (only when adapter is set)
      if (payload.adapter_name && payload.config_json) {
        try {
          payload.config = JSON.parse(payload.config_json);
        } catch (e) {
          throw new Error('Adapter config must be valid JSON');
        }
      } else if (payload.adapter_name) {
        payload.config = {};
      } else {
        payload.config = null;
      }
      delete payload.config_json;
      const base = `/admin/states/${stateId}/data-sources`;
      if (mode === 'create') {
        const res = await adminApi.post(base, payload);
        toast.success('Data source created');
        navigate(`/admin/states/${stateId}/data-sources/${res.data.id}`);
      } else {
        const res = await adminApi.put(`${base}/${sourceId}`, payload);
        setData({ ...EMPTY, ...res.data, county_id: res.data.county_id || '' });
        setOriginal(res.data);
        toast.success('Data source updated');
      }
    } catch (e) {
      const msg = extractError(e);
      setError(msg);
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  }

  async function markImported() {
    setActioning(true);
    try {
      const res = await adminApi.post(`/admin/states/${stateId}/data-sources/${sourceId}/mark-imported`);
      setData({ ...EMPTY, ...res.data, county_id: res.data.county_id || '' });
      setOriginal(res.data);
      toast.success('Marked as imported');
    } catch (e) { toast.error(extractError(e)); } finally { setActioning(false); }
  }
  async function markFailed() {
    const msg = window.prompt('Error message (optional):', '');
    setActioning(true);
    try {
      const res = await adminApi.post(`/admin/states/${stateId}/data-sources/${sourceId}/mark-failed`, { last_error: msg || '' });
      setData({ ...EMPTY, ...res.data, county_id: res.data.county_id || '' });
      setOriginal(res.data);
      toast.success('Marked as failed');
    } catch (e) { toast.error(extractError(e)); } finally { setActioning(false); }
  }

  const importSupported = original
    && (original.parsing_method === 'ai_scrape' || original.parsing_method === 'ai_crawl')
    && (original.source_type === 'supervisor_of_elections' || original.source_type === 'election_dates');
  const isCrawl = data.parsing_method === 'ai_crawl';

  async function runImport() {
    if (!importSupported) return;
    setActioning(true);
    try {
      const res = await adminApi.post(`/admin/states/${stateId}/data-sources/${sourceId}/run-import`);
      const job = res.data;
      // For ai_crawl the job comes back in 'running' state; navigate immediately so the user can watch
      if (job.status === 'running') {
        toast.success('Import job started — opening live status…');
      } else if (job.status === 'succeeded') {
        toast.success(`Import succeeded — ${job.items_extracted_count} record(s) staged for review`);
      } else {
        toast.error(`Import failed: ${job.error_message || 'unknown error'}`);
      }
      navigate(`/admin/states/${stateId}/import-jobs/${job.id}`);
    } catch (e) { toast.error(extractError(e)); } finally { setActioning(false); loadRecentJobs(); }
  }

  if (loading) {
    return (
      <>
        <PageHeader title={mode === 'create' ? 'New data source' : 'Edit data source'} />
        <div className="text-center p-5"><Spinner animation="border" /></div>
      </>
    );
  }

  const meta = dsStatusMeta(data.status);

  return (
    <>
      <PageHeader
        title={mode === 'create' ? 'New data source' : (original?.name || 'Edit data source')}
        breadcrumb={
          <span>
            <Link to="/admin/states" className="text-decoration-none">States</Link>
            {' / '}{state && <Link to={`/admin/states/${stateId}`} className="text-decoration-none">{state.name}</Link>}
            {' / '}<Link to={`/admin/states/${stateId}/data-management`} className="text-decoration-none">Data Management</Link>
            {' / '}<Link to={`/admin/states/${stateId}/data-sources`} className="text-decoration-none">Sources</Link>
            {' / '}{mode === 'create' ? 'New' : (original?.name || 'Edit')}
          </span>
        }
        actions={
          <div className="d-flex gap-2">
            {mode === 'edit' && recentJobs.length > 0 && (
              <Link to={`/admin/states/${stateId}/import-jobs/${recentJobs[0].id}`}>
                <Button variant="outline-primary" data-testid="data-source-view-latest-results-button">
                  <i className="bi bi-eye me-1" /> View latest results
                  {recentJobs[0].items_staged_count > 0 && <span className="ms-1">({recentJobs[0].items_staged_count})</span>}
                </Button>
              </Link>
            )}
            <Button variant="outline-secondary" onClick={() => navigate(`/admin/states/${stateId}/data-sources`)}>Cancel</Button>
            <Button variant="primary" onClick={handleSubmit} disabled={saving} data-testid="data-source-form-save-button">
              {saving ? <Spinner size="sm" animation="border" /> : (mode === 'create' ? 'Create source' : 'Save changes')}
            </Button>
          </div>
        }
      />
      <div className="page-content">
        {error && <Alert variant="danger">{error}</Alert>}
        <Form noValidate onSubmit={handleSubmit}>
          <Row className="g-3">
            <Col xs={12} lg={8}>
              <Card className="mb-3">
                <Card.Header>Source definition</Card.Header>
                <Card.Body>
                  <Row className="g-3">
                    <Col xs={12} md={8}>
                      <Form.Group>
                        <Form.Label>Name <span className="text-danger">*</span></Form.Label>
                        <Form.Control isInvalid={!!errors.name} value={data.name} onChange={(e) => update('name', e.target.value)} placeholder="e.g., FL Division of Elections — Calendar" data-testid="data-source-name-input" />
                        <Form.Control.Feedback type="invalid">{errors.name}</Form.Control.Feedback>
                      </Form.Group>
                    </Col>
                    <Col xs={12} md={4}>
                      <Form.Group>
                        <Form.Label>Source type <span className="text-danger">*</span></Form.Label>
                        <Form.Select isInvalid={!!errors.source_type} value={data.source_type} onChange={(e) => update('source_type', e.target.value)} data-testid="data-source-type-select">
                          <option value="">Choose…</option>
                          {SOURCE_TYPES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                        </Form.Select>
                        <Form.Control.Feedback type="invalid">{errors.source_type}</Form.Control.Feedback>
                      </Form.Group>
                    </Col>
                    <Col xs={12}>
                      <Form.Group>
                        <Form.Label>Source URL <span className="text-danger">*</span></Form.Label>
                        <Form.Control type="url" isInvalid={!!errors.source_url} value={data.source_url} onChange={(e) => update('source_url', e.target.value)} placeholder="https://…" data-testid="data-source-url-input" />
                        <Form.Control.Feedback type="invalid">{errors.source_url}</Form.Control.Feedback>
                      </Form.Group>
                    </Col>
                    <Col xs={6}>
                      <Form.Group>
                        <Form.Label>Parsing method</Form.Label>
                        <Form.Select value={data.parsing_method || 'manual'} onChange={(e) => update('parsing_method', e.target.value)} data-testid="data-source-method-select">
                          {PARSING_METHODS.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
                        </Form.Select>
                      </Form.Group>
                    </Col>
                    <Col xs={6}>
                      <Form.Group>
                        <Form.Label>Import frequency</Form.Label>
                        <Form.Control value={data.import_frequency || ''} onChange={(e) => update('import_frequency', e.target.value)} placeholder="e.g., daily, weekly, manual" data-testid="data-source-frequency-input" />
                        <div className="d-flex align-items-center gap-3 mt-2 flex-wrap">
                          <Form.Check
                            type="switch"
                            id="cadence-enabled-switch"
                            label={data.cadence_enabled ? 'Auto-run scheduled' : 'Auto-run disabled'}
                            checked={!!data.cadence_enabled}
                            onChange={(e) => update('cadence_enabled', e.target.checked)}
                            data-testid="data-source-cadence-toggle"
                          />
                          <Form.Select
                            size="sm"
                            value={data.kind || ''}
                            onChange={(e) => update('kind', e.target.value)}
                            style={{ maxWidth: 180 }}
                            data-testid="data-source-kind-select"
                          >
                            <option value="">— Kind (optional) —</option>
                            <option value="filings">Filings</option>
                            <option value="results">Results</option>
                            <option value="candidates">Candidates</option>
                            <option value="rules">Rules</option>
                          </Form.Select>
                          {data.next_run_at && (
                            <span className="text-muted small" data-testid="data-source-next-run">
                              Next run: <code>{new Date(data.next_run_at).toLocaleString()}</code>
                            </span>
                          )}
                          {data.last_run_status && (
                            <span className={`small ${data.last_run_status === 'failed' ? 'text-danger' : 'text-success'}`} data-testid="data-source-last-run-status">
                              Last run: {data.last_run_status}
                            </span>
                          )}
                        </div>
                      </Form.Group>
                    </Col>
                    <Col xs={12}>
                      <Form.Group>
                        <Form.Label>County scope {isCrawl ? '(ignored for AI Crawl)' : '(optional)'}</Form.Label>
                        <Form.Select
                          value={data.county_id || ''}
                          onChange={(e) => update('county_id', e.target.value)}
                          disabled={isCrawl}
                          data-testid="data-source-county-select"
                        >
                          <option value="">State-level (no specific county)</option>
                          {counties.map((c) => <option key={c.id} value={c.id}>{c.name}{c.county_code ? ` (${c.county_code})` : ''}</option>)}
                        </Form.Select>
                        <Form.Text>
                          {isCrawl
                            ? 'AI Crawl auto-matches each sub-page to its own county by name. The county field is ignored.'
                            : 'Leave blank for state-wide sources (e.g., a Division of Elections page).'}
                        </Form.Text>
                      </Form.Group>
                    </Col>
                    {isCrawl && (
                      <Col xs={12} md={6}>
                        <Form.Group>
                          <Form.Label>Max pages to crawl</Form.Label>
                          <Form.Control type="number" min={1} max={300}
                            value={data.crawl_max_pages || 80}
                            onChange={(e) => update('crawl_max_pages', parseInt(e.target.value, 10) || 80)}
                            data-testid="data-source-max-pages-input" />
                          <Form.Text>Safety cap. FL has 67 counties; 80 is a comfortable default.</Form.Text>
                        </Form.Group>
                      </Col>
                    )}
                  </Row>
                </Card.Body>
              </Card>
              <Card className="mb-3" data-testid="data-source-adapter-card">
                <Card.Header className="d-flex justify-content-between align-items-center">
                  <span>Adapter automation <span className="badge bg-info ms-1">Phase B</span></span>
                  {data.adapter_name && (
                    <span className="badge bg-success-subtle text-success border border-success-subtle">
                      <i className="bi bi-cpu me-1" /> Adapter enabled
                    </span>
                  )}
                </Card.Header>
                <Card.Body>
                  <Alert variant="light" className="border mb-3 py-2 px-3" style={{ fontSize: '0.85rem' }}>
                    Built-in adapters (FEC, Open States, …) fetch and normalize official data directly —
                    no AI scraping needed. Pick one below to enable the automated pipeline.
                    Leave blank to keep using the manual / AI-scrape flow above.
                  </Alert>
                  <Row className="g-3">
                    <Col xs={12} md={6}>
                      <Form.Group>
                        <Form.Label>Adapter</Form.Label>
                        <Form.Select
                          value={data.adapter_name || ''}
                          onChange={(e) => {
                            const name = e.target.value;
                            update('adapter_name', name);
                            // Helpful default config when the user picks an adapter for the first time.
                            if (name && !data.config_json) {
                              const presets = {
                                fec_candidates: '{\n  "cycles": [2024, 2026],\n  "candidate_status": ["C"]\n}',
                                open_states: '{\n  "cycles": [2026],\n  "chambers": ["upper", "lower"]\n}',
                              };
                              if (presets[name]) update('config_json', presets[name]);
                            }
                            // Auto-set the Kind tag from the adapter so the
                            // user doesn't have to remember to pick it. We
                            // map by adapter convention; users can still
                            // override via the Kind dropdown above.
                            if (name && !data.kind) {
                              const KIND_BY_ADAPTER = {
                                fec_candidates: 'candidates',
                                open_states: 'candidates',
                                fl_doe_candidates: 'candidates',
                                ballotpedia: 'candidates',
                                fec_results: 'results',
                                ap_results: 'results',
                                state_results: 'results',
                              };
                              const inferred = KIND_BY_ADAPTER[name] || 'candidates';
                              update('kind', inferred);
                            }
                            // When the user clears the adapter, also clear
                            // the auto-applied kind so the dropdown returns
                            // to "— Kind (optional) —".
                            if (!name && data.kind && data.adapter_name) {
                              update('kind', '');
                            }
                          }}
                          data-testid="data-source-adapter-select"
                        >
                          <option value="">— None (manual / AI scrape) —</option>
                          {adapters.map((a) => (
                            <option key={a.name} value={a.name}>{a.label || a.name}</option>
                          ))}
                        </Form.Select>
                        <Form.Text className="text-muted">
                          {adapters.length === 0
                            ? 'No adapters registered yet on this backend.'
                            : `${adapters.length} adapter${adapters.length === 1 ? '' : 's'} available`}
                        </Form.Text>
                      </Form.Group>
                    </Col>
                    <Col xs={12} md={6}>
                      <Form.Group>
                        <Form.Label className="d-flex align-items-center gap-2">
                          Auto-apply staged rows
                          <span className="badge bg-warning-subtle text-warning border border-warning-subtle small">
                            Skips review
                          </span>
                        </Form.Label>
                        <Form.Check
                          type="switch"
                          id="auto-apply-switch"
                          label={data.auto_apply ? 'Enabled — bypass Staging Inbox' : 'Disabled — rows land in Staging Inbox'}
                          checked={!!data.auto_apply}
                          onChange={(e) => update('auto_apply', e.target.checked)}
                          disabled={!data.adapter_name}
                          data-testid="data-source-auto-apply-toggle"
                        />
                        <Form.Text className="text-muted">
                          Enable for trusted official sources (FEC, state SoS, etc.) — rows are
                          applied directly with no manual review. Leave off when you want a
                          human in the loop.
                        </Form.Text>
                      </Form.Group>
                    </Col>
                    <Col xs={12}>
                      <Form.Group>
                        <Form.Label>Adapter config (JSON)</Form.Label>
                        <Form.Control
                          as="textarea"
                          rows={6}
                          value={data.config_json || ''}
                          onChange={(e) => update('config_json', e.target.value)}
                          placeholder='{\n  "cycles": [2024, 2026]\n}'
                          disabled={!data.adapter_name}
                          style={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontSize: '0.85rem' }}
                          data-testid="data-source-config-textarea"
                        />
                        <Form.Text className="text-muted">
                          {data.adapter_name === 'fec_candidates' && (
                            <>FEC accepts <code>cycles</code> (array of years), <code>candidate_status</code> (e.g. <code>{'["C"]'}</code>), and <code>office</code> (<code>H</code>, <code>S</code>, or <code>P</code>).</>
                          )}
                          {data.adapter_name === 'open_states' && (
                            <>Open States accepts <code>cycles</code> (array, default <code>{'[2026]'}</code>) and <code>chambers</code> (<code>{'["upper","lower"]'}</code>).</>
                          )}
                          {!data.adapter_name && 'Pick an adapter to see its accepted config keys.'}
                        </Form.Text>
                      </Form.Group>
                    </Col>
                    {mode === 'edit' && data.adapter_name && (
                      <Col xs={12}>
                        <div className="d-flex flex-wrap align-items-center gap-2">
                          <Button
                            variant="primary"
                            size="sm"
                            onClick={runAdapter}
                            disabled={runningAdapter || saving}
                            data-testid="data-source-run-adapter-button"
                          >
                            <i className="bi bi-play-fill me-1" />
                            {runningAdapter ? 'Running adapter…' : 'Run adapter now'}
                          </Button>
                          <span className="text-muted small">
                            Runs the pipeline against this source — fetch → normalize → diff →
                            {data.auto_apply ? ' apply directly.' : ' stage for review.'}
                          </span>
                        </div>
                        {original?.last_run_status && (
                          <div className="mt-2 small">
                            Last run:&nbsp;
                            <span className={original.last_run_status === 'failed' ? 'text-danger' : 'text-success'}>
                              {original.last_run_status}
                            </span>
                            {original.last_run_at && (
                              <span className="text-muted"> &middot; {fmtDate(original.last_run_at)}</span>
                            )}
                          </div>
                        )}
                      </Col>
                    )}
                  </Row>
                </Card.Body>
              </Card>
              <Card className="mb-3">
                <Card.Header>Field mapping & rules</Card.Header>
                <Card.Body>
                  <Form.Group className="mb-3">
                    <Form.Label>Field mapping notes</Form.Label>
                    <Form.Control as="textarea" rows={5} value={data.field_mapping_notes || ''} onChange={(e) => update('field_mapping_notes', e.target.value)} placeholder="e.g., Map .date-row .name to election_name, .date to election_date" data-testid="data-source-mapping-input" />
                    <Form.Text>Used by the future scraper/AI prompt. Plain English is fine.</Form.Text>
                  </Form.Group>
                  <Form.Group>
                    <Form.Label>Import rules</Form.Label>
                    <Form.Control as="textarea" rows={4} value={data.import_rules || ''} onChange={(e) => update('import_rules', e.target.value)} placeholder="e.g., Skip rows older than current year. Treat 'TBD' dates as null. Map party 'D'/'R' to Democratic/Republican." data-testid="data-source-rules-input" />
                  </Form.Group>
                </Card.Body>
              </Card>
            </Col>
            <Col xs={12} lg={4}>
              <Card className="mb-3" style={{ position: 'sticky', top: 12 }}>
                <Card.Header>Status</Card.Header>
                <Card.Body>
                  <Form.Group className="mb-3">
                    <Form.Label>Current status</Form.Label>
                    <Form.Select value={data.status || 'active'} onChange={(e) => update('status', e.target.value)} data-testid="data-source-status-select">
                      {DS_STATUSES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                    </Form.Select>
                    <Form.Text><span className={`status-badge ${meta.badge}`}>{meta.label}</span></Form.Text>
                  </Form.Group>
                  {mode === 'edit' && original && (
                    <>
                      <div className="mb-2">
                        <div className="text-muted" style={{ fontSize: '0.78rem' }}>Last imported</div>
                        <div>{original.last_imported_at ? fmtDate(original.last_imported_at) : <span className="text-muted">Never</span>}</div>
                      </div>
                      {original.last_error && (
                        <Alert variant="warning" className="py-2 px-2" style={{ fontSize: '0.85rem' }}>
                          <strong>Last error:</strong> {original.last_error}
                        </Alert>
                      )}
                      <div className="d-grid gap-2 mt-3">
                        {importSupported && (
                          <Button variant="primary" size="sm" onClick={runImport} disabled={actioning} data-testid="data-source-run-import-button">
                            <i className="bi bi-magic me-1" /> {actioning ? 'Running…' : 'Run AI import now'}
                          </Button>
                        )}
                        <Button variant="outline-success" size="sm" onClick={markImported} disabled={actioning} data-testid="data-source-mark-imported-button">
                          <i className="bi bi-check2-circle me-1" /> Mark as imported
                        </Button>
                        <Button variant="outline-danger" size="sm" onClick={markFailed} disabled={actioning} data-testid="data-source-mark-failed-button">
                          <i className="bi bi-x-circle me-1" /> Mark as failed
                        </Button>
                      </div>
                      <Form.Text className="d-block mt-2">
                        {importSupported
                          ? (isCrawl
                              ? 'AI Crawl will: (1) fetch the index URL, (2) ask Claude to list every county sub-page link, (3) fetch and extract each page in parallel, (4) for Supervisor of Elections, match each row to a county by name. If no sub-pages are found, it gracefully falls back to scraping the index page itself. You then review & approve.'
                              : 'AI Scrape will fetch the URL, ask Claude Sonnet 4.5 to extract records, and stage them for your review.')
                          : 'Future scraper service can call mark-imported/mark-failed automatically. Automated AI import is available for "AI Scrape"/"AI Crawl" + "Supervisor of Elections" / "Election Dates" sources.'}
                      </Form.Text>
                    </>
                  )}
                </Card.Body>
              </Card>
              {mode === 'edit' && (
                <Card className="mb-3">
                  <Card.Header className="d-flex justify-content-between align-items-center">
                    <span>Recent import jobs</span>
                    <Link to={`/admin/states/${stateId}/import-jobs?data_source_id=${sourceId}`}>
                      <Button size="sm" variant="link" className="p-0" data-testid="data-source-view-all-jobs-link">View all</Button>
                    </Link>
                  </Card.Header>
                  <Card.Body className="p-0">
                    {recentJobs.length === 0 ? (
                      <div className="text-muted px-3 py-3" style={{ fontSize: '0.88rem' }}>
                        No import jobs yet. Click <strong>“Run AI import now”</strong> to start one.
                      </div>
                    ) : (
                      <div className="list-group list-group-flush">
                        {recentJobs.map((j) => (
                          <Link key={j.id}
                            to={`/admin/states/${stateId}/import-jobs/${j.id}`}
                            className="list-group-item list-group-item-action"
                            data-testid={`data-source-recent-job-${j.id}`}
                          >
                            <div className="d-flex justify-content-between align-items-center">
                              <div>
                                <div className="fw-semibold" style={{ fontSize: '0.92rem' }}>
                                  <i className={`bi bi-${j.status === 'succeeded' ? 'check-circle text-success' : j.status === 'failed' ? 'x-circle text-danger' : j.status === 'running' ? 'arrow-clockwise text-primary' : 'clock text-muted'} me-1`} />
                                  {j.status === 'succeeded' ? `${j.items_staged_count} record${j.items_staged_count === 1 ? '' : 's'} staged` : (j.status.charAt(0).toUpperCase() + j.status.slice(1))}
                                </div>
                                <div className="text-muted" style={{ fontSize: '0.78rem' }}>
                                  {j.started_at ? fmtDate(j.started_at) : '—'}
                                  {j.parsing_method === 'ai_crawl' && j.pages_attempted ? ` · ${j.pages_succeeded}/${j.pages_attempted} pages` : ''}
                                </div>
                              </div>
                              <i className="bi bi-chevron-right text-muted" />
                            </div>
                          </Link>
                        ))}
                      </div>
                    )}
                  </Card.Body>
                </Card>
              )}
              <Card>
                <Card.Header>Tips</Card.Header>
                <Card.Body style={{ fontSize: '0.88rem' }}>
                  <ul className="ps-3 mb-0">
                    <li><strong>AI Scrape</strong> uses LLM + the URL + your field mapping notes to auto-extract.</li>
                    <li><strong>HTML Table</strong> for sites that publish clean tables.</li>
                    <li><strong>CSV / API</strong> for structured sources.</li>
                    <li>Leave the county empty for state-wide sources like the Division of Elections.</li>
                  </ul>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Form>
      </div>
    </>
  );
}
